import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { Notice } from '../../components/ui/Notice';
import { Spinner } from '../../components/ui/Spinner';
import { serverMessage } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { BoardPeriodControls, useBoardPeriod } from './BoardPeriodControls';
import type { StageCopy } from './content/divisions';
import { SheetCard } from './SheetCard';

export interface StageSheetsProps {
  stage: StageCopy;
}

const today = (): string => new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * The print-ready report sheets of one journey stage (plan §8.3): a period select using the shared period shape
 * (the Reports page's, plus Year to date), `board.stageSheets`, one `SheetCard` per sheet, and Print — the sheet set
 * renders inside `.print-document`, everything else on the page is `.no-print`.
 */
export const StageSheets = ({ stage }: StageSheetsProps) => {
  const { t } = useI18nTranslation();
  const periodState = useBoardPeriod();
  const { period, rangeWarning } = periodState;

  const sheets = useQuery({
    ...orpc.board.stageSheets.queryOptions({ input: { stage: stage.stage, period: period ?? { kind: 'YTD' } } }),
    enabled: period !== null,
    placeholderData: keepPreviousData,
  });
  const data = period && sheets.data?.stage === stage.stage ? sheets.data : undefined;
  const first = data?.sheets[0];

  return (
    <section
      className="space-y-3"
      data-stage-sheets={stage.stage}
    >
      <div className="no-print flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t('board.sheets.title', { title: stage.reportTitle })}</h2>
          <p className="text-xs text-muted-foreground">{t('board.sheets.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <BoardPeriodControls
            state={periodState}
            resolved={first ? { fromWeekId: first.period.fromWeek.id, toWeekId: first.period.toWeek.id } : null}
          />
          <Button
            size="sm"
            disabled={!data}
            onClick={() => window.print()}
            data-print-sheets
          >
            <Printer className="size-3.5" />
            {t('board.sheets.print')}
          </Button>
        </div>
      </div>

      {rangeWarning ? (
        <Notice
          tone="danger"
          className="no-print"
          onDismiss={periodState.clearWarning}
        >
          {rangeWarning}
        </Notice>
      ) : null}
      {sheets.isError ? (
        <Notice
          tone="danger"
          className="no-print"
        >
          {serverMessage(sheets.error) ?? t('board.loadFailed')}
        </Notice>
      ) : null}

      {!data ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner />
          {t('app.loading')}
        </div>
      ) : (
        <article
          className="print-document space-y-3"
          data-print-document
        >
          <header className="hidden print:block">
            <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{t('board.sheets.printing')}</p>
            <h2 className="text-base font-semibold">{t('board.sheets.title', { title: stage.reportTitle })}</h2>
            <p className="text-[10px] text-muted-foreground">
              {first ? t('board.sheets.periodLine', { period: first.period.label }) : null} · {t('board.sheets.generated', { date: today() })}
            </p>
          </header>
          <div className="print-sheet-grid grid gap-3 xl:grid-cols-2">
            {data.sheets.map((sheet) => (
              <SheetCard
                key={sheet.key}
                sheet={sheet}
              />
            ))}
          </div>
        </article>
      )}
    </section>
  );
};
